import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import backend from "../../backend";

const DOC_TYPE_LABELS = {
  facturaEmitida:  "Factura emitida",
  facturaRecibida: "Factura recibida",
  guiaDespacho:    "Guía de despacho",
  notaCredito:     "Nota de crédito",
  notaDebito:      "Nota de débito",
  boleta:          "Boleta",
  ocCliente:       "OC del cliente",
  cotizacionProveedor: "Cotización de proveedor",
  planoTecnico:    "Plano técnico",
  dosierCalidad:   "Dossier de calidad",
  fotoTrabajo:     "Foto de trabajo",
  xml:             "XML DTE",
  otro:            "Otro",
};

const DOC_TYPE_ICON = {
  facturaEmitida:  "🧾",
  facturaRecibida: "📄",
  guiaDespacho:    "🚚",
  notaCredito:     "🧾",
  notaDebito:      "🧾",
  boleta:          "🎫",
  ocCliente:       "📋",
  cotizacionProveedor: "💰",
  planoTecnico:    "📐",
  dosierCalidad:   "📋",
  fotoTrabajo:     "📷",
  xml:             "📄",
  otro:            "📎",
};

const TRIBUTARY_TYPES = [
  "facturaEmitida", "facturaRecibida", "guiaDespacho",
  "notaCredito", "notaDebito", "boleta",
];

const isTributary = docType => TRIBUTARY_TYPES.includes(docType);

const fmtSize = bytes => {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDate = d => d ? new Date(d).toLocaleDateString("es-CL", {
  day: "2-digit", month: "short", year: "numeric",
}) : "—";

/**
 * Props:
 *   refField   "workOrderRefs" | "customerOrderRefs" | "quotationRefs" |
 *              "pieceRef" | "pieceInstanceRef" | "purchaseOrderRefs"
 *   refId      string — ObjectId del padre
 *   defaultType  clave de DOC_TYPE_LABELS
 */
const DocumentAttacher = ({ refField, refId, defaultType = "otro" }) => {
  const [docs, setDocs]               = useState([]);
  const [trashedDocs, setTrashedDocs] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showTrash, setShowTrash]     = useState(false);
  const [showUpload, setShowUpload]   = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [error, setError]             = useState("");

  // upload form
  const [file, setFile]   = useState(null);
  const [type, setType]   = useState(defaultType);
  // F1 — datos de una cotización de proveedor. Viven acá y no en un componente aparte
  // porque sólo existen mientras se sube ESE archivo.
  const [cotProveedor, setCotProveedor] = useState("");
  const [cotMonto, setCotMonto]         = useState("");
  const [cotValidez, setCotValidez]     = useState("");
  const [proveedores, setProveedores]   = useState([]);
  const [label, setLabel] = useState("");
  const fileInputRef      = useRef();

  // soft-delete modal (docs tributarios)
  const [deleteTarget, setDeleteTarget]   = useState(null);
  const [deleteReason, setDeleteReason]   = useState("");
  const [deleting, setDeleting]           = useState(false);

  // permanent-delete modal (desde papelera)
  const [permanentTarget, setPermanentTarget] = useState(null);

  const load = useCallback(async () => {
    if (!refId) return;
    setLoading(true);
    try {
      const [activeRes, trashRes] = await Promise.all([
        backend.get(`document?${refField}=${refId}`),
        backend.get(`document?${refField}=${refId}&onlyDeleted=true`),
      ]);
      setDocs(activeRes.data || []);
      setTrashedDocs(trashRes.data || []);
    } catch (e) {
      console.error("[DocumentAttacher] load error:", e);
    }
    setLoading(false);
  }, [refField, refId]);

  useEffect(() => { load(); }, [load]);

  // BUG 2: Escape cierra los modales
  useEffect(() => {
    if (!deleteTarget && !permanentTarget) return;
    const handleEsc = e => {
      if (e.key === "Escape" && !deleting) {
        setDeleteTarget(null);
        setPermanentTarget(null);
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [deleteTarget, permanentTarget, deleting]);

  // La lista de proveedores se pide sólo cuando el tipo elegido la necesita: es una
  // consulta que la mayoría de los adjuntos no usa.
  useEffect(() => {
    if (type !== "cotizacionProveedor" || proveedores.length) return;
    let vivo = true;
    backend.get("entity?type=provider")
      .then(({ data }) => { if (vivo) setProveedores(Array.isArray(data) ? data : (data?.entities || [])); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [type, proveedores.length]);

  // BUG 3: auto-salir del modo papelera cuando queda vacía
  useEffect(() => {
    if (showTrash && trashedDocs.length === 0) {
      setShowTrash(false);
    }
  }, [trashedDocs, showTrash]);

  // ── Upload ──────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (!file) { setError("Seleccioná un archivo"); return; }
    setError("");
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("docType", type);
    if (label) formData.append("label", label);
    formData.append(refField, refId);
    if (type === "cotizacionProveedor") {
      if (cotProveedor) formData.append("cotProveedor", cotProveedor);
      if (cotMonto)     formData.append("cotMonto", cotMonto);
      if (cotValidez)   formData.append("cotValidez", cotValidez);
    }
    try {
      await backend.post("document/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setShowUpload(false);
      setFile(null);
      setLabel("");
      setCotProveedor(""); setCotMonto(""); setCotValidez("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      await load();
    } catch (e) {
      setError(e.response?.data?.error || "Error al subir");
    }
    setUploading(false);
  };

  // ── Delete ──────────────────────────────────────────────────────────
  const handleDeleteClick = doc => {
    if (isTributary(doc.docType)) {
      setDeleteTarget(doc);
      setDeleteReason("");
    } else {
      if (window.confirm(
        `¿Eliminar "${doc.originalName}"?\nEsto borra el archivo permanentemente.`
      )) {
        performDelete(doc, { reason: null, force: false });
      }
    }
  };

  const performDelete = async (doc, { reason, force }) => {
    setDeleting(true);
    try {
      await backend.delete(`document/${doc._id}`, {
        data: { reason, force, deletedBy: "catalina" },
      });
      setDeleteTarget(null);
      setDeleteReason("");
      setPermanentTarget(null);
      await load();
    } catch (e) {
      alert(e.response?.data?.error || "Error al eliminar");
    }
    setDeleting(false);
  };

  const handleConfirmSoftDelete = () => {
    if (deleteReason.trim().length < 10) return;
    performDelete(deleteTarget, { reason: deleteReason.trim(), force: false });
  };

  const handleRestore = async doc => {
    try {
      await backend.post(`document/${doc._id}/restore`);
      await load();
    } catch (e) {
      console.error("[DocumentAttacher] restore error:", e);
    }
  };

  const confirmPermanentDelete = () => {
    if (!permanentTarget) return;
    performDelete(permanentTarget, { reason: null, force: true });
  };

  // ── Render ──────────────────────────────────────────────────────────
  const rowsToShow = showTrash ? trashedDocs : docs;

  return (
    <div className="bini-doc-attacher">
      {/* Header */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: 10,
        flexWrap: "wrap", gap: 8,
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#ccc" }}>
          Documentos adjuntos ({docs.length})
        </span>
        <div style={{ display: "flex", gap: 6 }}>
          {trashedDocs.length > 0 && (
            <button type="button" onClick={() => setShowTrash(v => !v)}
                    style={btnStyle("#333", "#aaa")}>
              {showTrash ? "📁 Ver activos" : `🗑 Papelera (${trashedDocs.length})`}
            </button>
          )}
          {!showUpload && !showTrash && (
            <button type="button" onClick={() => setShowUpload(true)}
                    style={btnStyle("#2c2c2c", "#aaa")}>
              📎 Adjuntar
            </button>
          )}
        </div>
      </div>

      {/* Banner papelera — BUG 3: solo si hay docs en papelera */}
      {showTrash && trashedDocs.length > 0 && (
        <div style={{
          padding: "8px 12px", marginBottom: 10,
          background: "#3e2a0d", border: "1px solid #8a6d3b",
          borderRadius: 6, fontSize: 11, color: "#f0c36d",
        }}>
          🗑 Documentos eliminados — archivados por requisito SII (6 años).
        </div>
      )}

      {/* Lista */}
      {loading && <div style={{ color: "#cbd5e1", fontSize: 11 }}>Cargando...</div>}

      {!loading && rowsToShow.length === 0 && (
        <div style={{ color: "#999", fontSize: 11, padding: "8px 0" }}>
          {showTrash ? "Papelera vacía." : "Sin documentos adjuntos."}
        </div>
      )}

      {!loading && rowsToShow.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {rowsToShow.map(d => (
            <div key={d._id} style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "8px 10px",
              background: d.deletedAt ? "#1a0d0d" : "#111",
              border: `1px solid ${d.deletedAt ? "#5a2020" : "#2a2a2a"}`,
              borderRadius: 6,
              opacity: d.deletedAt ? 0.8 : 1,
            }}>
              <span style={{ fontSize: 15, flexShrink: 0 }}>
                {DOC_TYPE_ICON[d.docType] || "📎"}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: "#ddd", display: "flex", alignItems: "center", gap: 6 }}>
                  <span>{DOC_TYPE_LABELS[d.docType] || d.docType}</span>
                  {d.docType === "cotizacionProveedor" && (d.cotizacionProveedor?.monto || d.cotizacionProveedor?.validezHasta) && (
                    <span style={{ color: "#cbd5e1" }}>
                      {d.cotizacionProveedor.monto
                        ? ` · $${Number(d.cotizacionProveedor.monto).toLocaleString("es-CL")}`
                        : ""}
                      {d.cotizacionProveedor.validezHasta
                        ? ` · vale hasta ${new Date(d.cotizacionProveedor.validezHasta).toLocaleDateString("es-CL")}`
                        : ""}
                    </span>
                  )}
                  {d.label && <span style={{ color: "#cbd5e1" }}>— {d.label}</span>}
                  {isTributary(d.docType) && (
                    <span style={{
                      padding: "1px 4px", fontSize: 9, fontWeight: 700,
                      background: "#2a1a0d", color: "#f39c12", borderRadius: 3,
                    }}>SII</span>
                  )}
                </div>
                <div style={{ fontSize: 10, color: "#aaa", marginTop: 2 }}>
                  {d.originalName} · {fmtSize(d.size)}
                </div>
                {d.deletedAt && (
                  <div style={{ fontSize: 10, color: "#c0504d", marginTop: 3 }}>
                    Eliminado {fmtDate(d.deletedAt)} · {d.deleteReason}
                  </div>
                )}
              </div>

              {!d.deletedAt && (
                <>
                  <a href={d.url} target="_blank" rel="noopener noreferrer"
                     style={{ ...btnStyle("#1a1a1a", "#3498db"), textDecoration: "none" }}>
                    Ver
                  </a>
                  <button type="button" onClick={() => handleDeleteClick(d)}
                          style={{ ...btnStyle("none", "#e74c3c"), border: "none", padding: "4px 6px" }}>
                    ✕
                  </button>
                </>
              )}

              {d.deletedAt && (
                <>
                  <a href={d.url} target="_blank" rel="noopener noreferrer"
                     style={{ ...btnStyle("#1a1a1a", "#888"), textDecoration: "none", fontSize: 10 }}>
                    Ver
                  </a>
                  <button type="button" onClick={() => handleRestore(d)}
                          style={{ ...btnStyle("#112211", "#2ecc71"), fontSize: 10 }}>
                    ↩ Restaurar
                  </button>
                  <button type="button" onClick={() => setPermanentTarget(d)}
                          title="Eliminar definitivamente"
                          style={{ ...btnStyle("none", "#e74c3c"), border: "none", padding: "4px 6px", fontSize: 10 }}>
                    ✕✕
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Formulario de upload */}
      {showUpload && (
        <div style={{
          marginTop: 10, padding: 12,
          background: "#0d0d0d", border: "1px solid #2e2e2e", borderRadius: 6,
        }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
            <div className="bini-field">
              <label>Tipo</label>
              <select className="bini-input" value={type} onChange={e => setType(e.target.value)}>
                {Object.entries(DOC_TYPE_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            <div className="bini-field">
              <label>Etiqueta (opcional)</label>
              <input className="bini-input" value={label} onChange={e => setLabel(e.target.value)}
                     placeholder="Ej: Factura 1812" />
            </div>
          </div>
          {/* F1 — sólo para la cotización del proveedor. Los tres campos son opcionales:
              subir el PDF y no acordarse del monto tiene que seguir siendo posible. */}
          {type === "cotizacionProveedor" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
              <div className="bini-field">
                <label>Proveedor (opcional)</label>
                <select className="bini-input" value={cotProveedor} onChange={e => setCotProveedor(e.target.value)}>
                  <option value="">—</option>
                  {proveedores.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
              </div>
              <div className="bini-field">
                <label>Monto (opcional)</label>
                <input className="bini-input" type="number" min="0" value={cotMonto}
                       onChange={e => setCotMonto(e.target.value)} placeholder="$" />
              </div>
              <div className="bini-field">
                <label>Vale hasta (opcional)</label>
                <input className="bini-input" type="date" value={cotValidez}
                       onChange={e => setCotValidez(e.target.value)} />
              </div>
            </div>
          )}

          <div className="bini-field" style={{ marginBottom: 10 }}>
            <label>Archivo (PDF, imagen o XML — máx 20 MB)</label>
            <input type="file" ref={fileInputRef}
                   accept=".pdf,.jpg,.jpeg,.png,.webp,.xml"
                   onChange={e => setFile(e.target.files[0] || null)}
                   style={{ fontSize: 12, color: "#ccc" }} />
            {file && (
              <div style={{ fontSize: 10, color: "#cbd5e1", marginTop: 4 }}>
                {file.name} · {fmtSize(file.size)}
              </div>
            )}
          </div>
          {error && <div style={{ color: "#e74c3c", fontSize: 11, marginBottom: 8 }}>{error}</div>}
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
            <button type="button"
                    style={btnStyle("#2a2a2a", "#aaa")}
                    onClick={() => { setShowUpload(false); setError(""); setFile(null); }}>
              Cancelar
            </button>
            <button type="button"
                    style={{ ...btnStyle(uploading || !file ? "#222" : "#e30613", "#fff"), opacity: uploading || !file ? 0.5 : 1 }}
                    disabled={uploading || !file}
                    onClick={handleUpload}>
              {uploading ? "Subiendo…" : "Subir"}
            </button>
          </div>
        </div>
      )}

      {/* Modal: soft delete con razón — BUG 1: Portal a body para escapar del modal padre */}
      {deleteTarget && createPortal(
        <div style={overlayStyle} onClick={() => !deleting && setDeleteTarget(null)}>
          <div style={{ ...modalStyle, borderColor: "#e74c3c" }}
               onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#fff", marginBottom: 4 }}>
              Eliminar documento tributario
            </div>
            <div style={{ fontSize: 12, color: "#aaa", marginBottom: 14 }}>
              {DOC_TYPE_LABELS[deleteTarget.docType]} · {deleteTarget.originalName}
            </div>
            <div style={{
              padding: "10px 12px", marginBottom: 14,
              background: "#2a1a0d", border: "1px solid #8a6d3b",
              borderRadius: 6, fontSize: 11, color: "#f0c36d",
            }}>
              ⚠️ Los documentos tributarios quedan en papelera (no se eliminan físicamente). Requisito SII — 6 años de conservación.
            </div>
            <div className="bini-field">
              <label>Razón de eliminación (mínimo 10 caracteres)</label>
              <textarea className="bini-input"
                        value={deleteReason}
                        onChange={e => setDeleteReason(e.target.value)}
                        rows={3}
                        placeholder="Ej: Factura duplicada, emitida con error en folio 1812…"
                        style={{ resize: "vertical", minHeight: 70, fontSize: 12 }}
                        autoFocus />
              <div style={{ fontSize: 10, color: deleteReason.length < 10 ? "#e74c3c" : "#888", marginTop: 4 }}>
                {deleteReason.length} / 10 mínimo
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 14 }}>
              <button type="button" style={btnStyle("#2a2a2a", "#aaa")}
                      onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancelar
              </button>
              <button type="button"
                      style={{ ...btnStyle("#e74c3c", "#fff"), opacity: deleting || deleteReason.trim().length < 10 ? 0.5 : 1 }}
                      disabled={deleting || deleteReason.trim().length < 10}
                      onClick={handleConfirmSoftDelete}>
                {deleting ? "Moviendo…" : "Mover a papelera"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal: eliminación definitiva — BUG 1: Portal a body */}
      {permanentTarget && createPortal(
        <div style={overlayStyle} onClick={() => !deleting && setPermanentTarget(null)}>
          <div style={{ ...modalStyle, borderColor: "#e74c3c", borderWidth: 2 }}
               onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#e74c3c", marginBottom: 10 }}>
              ⚠ Eliminar definitivamente
            </div>
            <div style={{ fontSize: 12, color: "#ccc", marginBottom: 16, lineHeight: 1.6 }}>
              Esto borra el archivo físico de forma <strong>irreversible</strong>.
              Se perderá la trazabilidad para auditorías.<br /><br />
              <strong>{DOC_TYPE_LABELS[permanentTarget.docType]}</strong>: {permanentTarget.originalName}<br />
              Eliminado el: {fmtDate(permanentTarget.deletedAt)}<br />
              Razón: "{permanentTarget.deleteReason}"
            </div>
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
              <button type="button" style={btnStyle("#2a2a2a", "#aaa")}
                      onClick={() => setPermanentTarget(null)} disabled={deleting}>
                Cancelar
              </button>
              <button type="button"
                      style={{ ...btnStyle("#e74c3c", "#fff"), opacity: deleting ? 0.5 : 1 }}
                      disabled={deleting} onClick={confirmPermanentDelete}>
                {deleting ? "Eliminando…" : "Eliminar para siempre"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// ── Helpers de estilo ──────────────────────────────────────────────────────────
const btnStyle = (bg, color) => ({
  background: bg,
  color,
  border: `1px solid ${color}33`,
  borderRadius: 5,
  padding: "5px 10px",
  fontSize: 11,
  cursor: "pointer",
  whiteSpace: "nowrap",
  fontFamily: "inherit",
});

const overlayStyle = {
  position: "fixed", inset: 0,
  background: "rgba(0,0,0,0.75)", zIndex: 10000,
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: 20,
};

const modalStyle = {
  background: "#1a1a1a",
  border: "1px solid #444",
  borderRadius: 8,
  padding: 20,
  width: "100%",
  maxWidth: 480,
  boxShadow: "0 4px 24px rgba(0,0,0,0.6)",
};

export default DocumentAttacher;
